'use client';

import { useMemo } from 'react';
import { BigNum, OrderType } from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	MarketId,
	getLimitPriceFromOracleOffset,
} from '@velocity-exchange/common';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import useGetTickSizeForMarket, {
	getTickSizeDecimalPlaces,
} from 'src/hooks/useGetTickSizeForMarket';
import { AccountOrder } from '../types';

interface PriceCellProps {
	order: AccountOrder;
}

const EMPTY_VALUE = <span className="text-text-tertiary">-</span>;

export function PriceCell({ order }: PriceCellProps) {
	const getOraclePrice = useGetOraclePriceForMarket();
	const getTickSizeForMarket = useGetTickSizeForMarket();

	const marketId = new MarketId(order.marketIndex, order.marketType);
	const tickSize = getTickSizeForMarket(marketId);
	const tickSizeDecimalPlaces = getTickSizeDecimalPlaces(tickSize);
	const oraclePrice = getOraclePrice(marketId) as BigNum;

	const limitPriceToUse = useMemo(() => {
		return getLimitPriceFromOracleOffset(order, oraclePrice);
	}, [oraclePrice, order]);

	if (
		ENUM_UTILS.match(order.orderType, OrderType.MARKET) ||
		ENUM_UTILS.match(order.orderType, OrderType.TRIGGER_MARKET)
	) {
		return <span>Market</span>;
	}

	if (limitPriceToUse.eqZero()) {
		return EMPTY_VALUE;
	}

	if (order.oraclePriceOffset) {
		return (
			<Tooltip
				content={
					<span>
						{`Oracle ${
							order.oraclePriceOffset.gtZero() ? '+' : ''
						}${order.oraclePriceOffset.print()}`}
					</span>
				}
			>
				<NumberDisplay
					value={limitPriceToUse}
					type="currency"
					decimals={tickSizeDecimalPlaces}
					trimZeroes
				/>
			</Tooltip>
		);
	}

	return (
		<NumberDisplay
			value={limitPriceToUse}
			type="currency"
			decimals={tickSizeDecimalPlaces}
			trimZeroes
		/>
	);
}
